import { cn } from '@sohanemon/utils';
import type { ComponentProps, ReactNode } from 'react';

type PageHeroProps = ComponentProps<'header'> & {
	/** Route path displayed as `~/path` provenance line */
	path: string;
	/** Optional status badge shown after the provenance slash */
	status?: string;
	/** Main heading text */
	children: ReactNode;
	/** Subtitle paragraph below the heading */
	sub?: string;
	/** Left side of the bottom metadata bar */
	meta?: ReactNode;
	/** Right side of the bottom metadata bar (e.g. fake sys readout) */
	metaRight?: ReactNode;
};

export function PageHero({
	path,
	status,
	children,
	sub,
	meta,
	metaRight,
	className,
	...props
}: PageHeroProps) {
	const hasMetaBar = meta || metaRight;

	return (
		<header className={cn('mb-12', className)} {...props}>
			<div className="flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-2xs text-muted-foreground uppercase tracking-widest">
				<span className="text-primary">~/{path}</span>
				{status && (
					<>
						<span className="text-primary/40">/</span>
						<span className="text-success">{status}</span>
					</>
				)}
			</div>

			<h1 className="mt-4 max-w-3xl font-mono font-semibold text-3xl text-primary tracking-tight md:text-5xl">
				{children}
			</h1>

			{sub && (
				<p className="mt-4 max-w-2xl text-foreground/75 lg:text-lg">{sub}</p>
			)}

			{hasMetaBar && (
				<div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-border/50 border-y py-3 font-mono text-2xs text-muted-foreground uppercase tracking-widest">
					<span>{meta}</span>
					{metaRight && (
						<span className="text-success tabular-nums">{metaRight}</span>
					)}
				</div>
			)}
		</header>
	);
}
